import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CountriesDropdown } from "../components/dropdowns/CountriesDropdown";
import CheckBox from "../components/inputs/CheckBox";
import DateOfBirthPicker from "../components/inputs/DateOfBirthPicker";
import GlobalTextInputField from "../components/inputs/GlobalTextInputField";
import AccessInfoBadges from "../components/layout/AccessInfoBadges";
import ExchangeInfoPanel from "../components/layout/ExchangeInfoPanel";
import TermsAndConditionsModal from "../components/modals/TermsAndConditionsModal";
import CryptoDepositPopup from "../components/order/CryptoDepositPopup";
import OrderStatusCard from "../components/order/OrderStatusCard";
import { commonApiServices } from "../services/api/Common";
import { primeFiatApiServices } from "../services/api/PrimeFiat";
import { GetCountriesListData } from "../services/models/GetCountriesList";
import { GetFiatInstanceDetailsData } from "../services/models/PrimeFiat";
import webSocketService from "../services/websocket/WebsocketService";
import { isValidDobDate } from "../utils/dob";
import {
  getCadPaymentRedirect,
  shouldShowForm,
  shouldShowStatus,
} from "../utils/orderStatus";
import AcceptableUsePolicyModal from "../components/modals/AcceptableUsePolicyModal";
import RiskDisclosureModal from "../components/modals/RiskDisclosureModal";

function formatAmount(value?: number | string, digits = 2) {
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function currencyPrefix(symbol?: string) {
  if (symbol === "CAD") return "$";
  return "€";
}

const dropdownProps = {
  color: "bg-page-body",
  baseColor: "bg-page-body",
  borderColor: "border-border-subtle",
  expandedBorderColor: "border-border-subtle",
  titleColor: "text-text-subtle",
};

function ReviewPurchasePage() {
  const { instanceId, region } = useParams();
  const navigate = useNavigate();
  const wsInitializedRef = useRef(false);

  const [instanceData, setInstanceData] =
    useState<GetFiatInstanceDetailsData>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [walletConfirmed, setWalletConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [aupAccepted, setAupAccepted] = useState(false);
  const [riskAccepted, setRiskAccepted] = useState(false);
  const [accuracyAccepted, setAccuracyAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [aupOpen, setAupOpen] = useState(false);
  const [riskOpen, setRiskOpen] = useState(false);
  const [countries, setCountries] = useState<GetCountriesListData[]>([]);
  const [depositOpen, setDepositOpen] = useState(false);
  const [acceptedDeposit, setAcceptedDeposit] = useState<{
    paymentUrl: string;
    orderId?: string;
  } | null>(null);
  const providerRedirectedRef = useRef<string | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [residenceCountryId, setResidenceCountryId] = useState<
    string | number | undefined
  >();
  const [birthCountryId, setBirthCountryId] = useState<
    string | number | undefined
  >();

  const locked = useMemo(() => {
    const u = instanceData?.userDetails || {};
    return {
      firstName: !!u.firstName,
      lastName: !!u.lastName,
      email: !!u.email,
      dob: !!u.dob,
      country: !!u.country,
      countryOfBirth: !!(u.countryOfBirth || u.countryOfBirthName),
    };
  }, [instanceData]);

  const isExternal = instanceData?.transferType !== "internal";
  const isPayout = instanceData?.isBuying === 0;
  const [showInverseRate, setShowInverseRate] = useState(false);
  const quotedRate = (() => {
    const rate = Number(instanceData?.rate);
    if (!Number.isFinite(rate) || rate === 0) return undefined;
    const cryptoPerFiat =
      instanceData?.fiatCurrencySymbol === "CAD" ? 1 / rate : rate;
    return showInverseRate ? 1 / cryptoPerFiat : cryptoPerFiat;
  })();
  const rateBaseSymbol = showInverseRate
    ? instanceData?.cryptoCurrencySymbol
    : instanceData?.fiatCurrencySymbol;
  const rateQuoteSymbol = showInverseRate
    ? instanceData?.fiatCurrencySymbol
    : instanceData?.cryptoCurrencySymbol;

  const findCountry = useCallback(
    (value?: string | null) => {
      if (!value) return undefined;
      const needle = String(value).toLowerCase().trim();
      return countries.find(
        (c) =>
          c.country.toLowerCase() === needle ||
          c.countryShort?.toLowerCase() === needle ||
          c.countryShort2?.toLowerCase() === needle ||
          c.countryCode?.toLowerCase() === needle,
      );
    },
    [countries],
  );
  const fetchInstanceDetails = useCallback(async () => {
    if (!instanceId || !region) {
      setError("Missing instance id.");
      setLoading(false);
      return;
    }

    try {
      const response = await primeFiatApiServices.getInstanceDetails(
        instanceId,
        region,
      );
      setInstanceData(response.data);
      setError("");
    } catch (err: any) {
      setError(
        err?.response?.data?.msg ||
          err?.message ||
          "Failed to load purchase details.",
      );
    } finally {
      setLoading(false);
    }
  }, [instanceId, region]);

  useEffect(() => {
    setLoading(true);
    fetchInstanceDetails();
  }, [fetchInstanceDetails]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await commonApiServices.getCountriesList({
          isEuropeFlow: region !== "cad",
        });
        if (!cancelled) setCountries(response.data || []);
      } catch {
        if (!cancelled) setCountries([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [region]);

  useEffect(() => {
    if (!instanceData?.userDetails) return;
    const u = instanceData.userDetails;
    if (u.firstName) setFirstName(u.firstName);
    if (u.lastName) setLastName(u.lastName);
    if (u.email) setEmail(u.email);
    if (u.dob) setDob(u.dob);

    if (countries.length === 0) return;

    const residenceMatch = findCountry(u.country);
    if (residenceMatch) setResidenceCountryId(residenceMatch.id);

    const birthMatch = findCountry(u.countryOfBirth || u.countryOfBirthName);
    if (birthMatch) setBirthCountryId(birthMatch.id);
  }, [instanceData, countries, findCountry]);

  useEffect(() => {
    if (!instanceId || !region || wsInitializedRef.current) return;

    let cancelled = false;
    const eventName =
      region === "cad" ? "cad_kyc_status_update" : "eur_kyc_status_update";

    const setupSocket = async () => {
      await webSocketService.connect();
      if (cancelled) return;

      webSocketService.subscribeToRoom(instanceId);
      wsInitializedRef.current = true;

      const handleStatusUpdate = (data: {
        instanceId?: string;
        eventId?: number | string;
        eventDescription?: string;
        kycStatus?: string;
        kycSessionUrl?: string;
      }) => {
        if (data?.instanceId && data.instanceId !== instanceId) return;

        setInstanceData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            eventId: data.eventId != null ? Number(data.eventId) : prev.eventId,
            eventDescription: data.eventDescription ?? prev.eventDescription,
            kycStatus: data.kycStatus ?? prev.kycStatus,
            kycSessionUrl: data.kycSessionUrl ?? prev.kycSessionUrl,
          };
        });

        fetchInstanceDetails();
      };

      webSocketService.onMessage(eventName, handleStatusUpdate);
    };

    setupSocket();

    const pollId = window.setInterval(() => {
      fetchInstanceDetails();
    }, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(pollId);
      webSocketService.disconnect();
      wsInitializedRef.current = false;
    };
  }, [instanceId, region, fetchInstanceDetails]);

  const showForm = shouldShowForm(region, instanceData);
  const showStatus = shouldShowStatus(region, instanceData);

  useEffect(() => {
    const target = getCadPaymentRedirect(region, instanceData);
    if (!target || target === window.location.href) return;
    if (providerRedirectedRef.current === target) return;
    providerRedirectedRef.current = target;
    window.open(target, "_blank", "noopener,noreferrer");
  }, [region, instanceData]);

  const depositPaymentUrl =
    instanceData?.paymentUrl || acceptedDeposit?.paymentUrl || "";
  // const showBackHome = instanceData?.userId === 20379;
  const showBackHome = true;

  const residenceCountry = countries.find((c) => c.id === residenceCountryId);
  const birthCountry = countries.find((c) => c.id === birthCountryId);

  const formValid = Boolean(
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    isValidDobDate(dob) &&
    residenceCountry &&
    birthCountry,
  );

  const canPay =
    !!instanceData &&
    formValid &&
    termsAccepted &&
    aupAccepted &&
    riskAccepted &&
    accuracyAccepted &&
    walletConfirmed;

  const handleGoHome = () => {
    sessionStorage.removeItem("obd_instance_id");
    sessionStorage.removeItem("obd_region");
    navigate("/instance");
  };

  const handlePayNow = async () => {
    if (!instanceId || !instanceData || !canPay || !residenceCountry) return;

    setSubmitting(true);
    setError("");
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const response = isPayout
        ? await primeFiatApiServices.acceptPayout(
            {
              instanceId,
              fullName,
              dateOfBirth: dob,
              nationality:
                residenceCountry.countryShort2 ||
                residenceCountry.countryShort ||
                residenceCountry.country,
              emailId: email.trim(),
              url: window.location.href,
              iban: instanceData.userDetails?.iban || undefined,
              ...(region === "cad"
                ? {
                    province: String(
                      sessionStorage.getItem("obd_payout_province") ||
                        instanceData.userDetails?.province ||
                        "",
                    ),
                    mobile: String(
                      sessionStorage.getItem("obd_payout_mobile") ||
                        instanceData.userDetails?.mobile ||
                        "",
                    ).replace(/\D/g, ""),
                  }
                : {}),
            },
            region || "eur",
          )
        : await primeFiatApiServices.acceptPayin(
            {
              instanceId,
              fullName,
              dateOfBirth: dob,
              nationality:
                residenceCountry.countryShort ||
                residenceCountry.countryShort2 ||
                residenceCountry.country,
              emailId: email.trim(),
              url: window.location.href,
            },
            region || "eur",
          );

      sessionStorage.setItem("obd_instance_id", instanceId);
      if (region) sessionStorage.setItem("obd_region", region);
      await fetchInstanceDetails();

      if (response.data?.paymentUrl) {
        setAcceptedDeposit({
          paymentUrl: response.data.paymentUrl,
          orderId: response.data.orderId,
        });
        return;
      }

      const paymentLink = response.data?.url;
      if (paymentLink && paymentLink !== window.location.href) {
        window.open(paymentLink, "_blank", "noopener,noreferrer");
        return;
      }

      if (!response.data?.ReferenceNumber) {
        setError(response.msg || "Unable to continue to payment.");
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.msg ||
          err?.message ||
          "Failed to continue to payment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sectionTitle = (text: string) => (
    <h3 className="text-[#1418A8] text-[12px] font-bold mb-[8px]">{text}</h3>
  );

  return (
    <div className="relative h-dvh w-full overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
        <div className="noise w-full h-full" />
      </div>

      <div className="relative h-full w-full flex items-stretch justify-center px-[16px] lg:px-[80px] py-[20px] lg:py-[40px] overflow-hidden">
        <div className="w-full h-full flex flex-col lg:flex-row gap-[24px] justify-between items-stretch min-h-0">
          <ExchangeInfoPanel
            headline={
              showStatus ? (
                <>Order status</>
              ) : (
                <>
                  <span className="text-[#2E9B4F]">Review</span> your{" "}
                  {isPayout ? "payout" : "purchase"}
                </>
              )
            }
          />

          <div
            className="w-full max-w-[560px] mx-auto lg:mx-0 flex-1 min-h-0 rounded-[20px] flex flex-col overflow-hidden bg-bg-page-body"
            style={{ boxShadow: "5px 5px 25px 0 rgba(0, 0, 0, 0.10)" }}
          >
            {loading ? (
              <div className="flex-1 flex items-center justify-center p-[24px] text-text-subtle text-[14px]">
                Loading your purchase details...
              </div>
            ) : error && !instanceData ? (
              <div className="flex-1 flex flex-col">
                <div className="flex-1 flex items-center justify-center p-[24px] text-text-status-red text-[14px] text-center">
                  {error}
                </div>
                <div className="px-[24px] pb-[24px] shrink-0">
                  <button
                    type="button"
                    onClick={handleGoHome}
                    className="w-full h-[44px] rounded-full text-[14px] font-semibold text-text-base border border-border-subtle bg-transparent"
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            ) : showStatus && instanceData ? (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <OrderStatusCard
                    instanceData={instanceData}
                    region={region}
                  />
                </div>
                {depositPaymentUrl || showBackHome ? (
                  <div className="px-[24px] pb-[24px] pt-[8px] shrink-0 space-y-[8px]">
                    {depositPaymentUrl ? (
                      <button
                        type="button"
                        onClick={() => setDepositOpen(true)}
                        className="w-full h-[44px] rounded-full text-white text-[14px] font-semibold"
                        style={{
                          background:
                            "linear-gradient(90deg, #0E18A7 0%, #5E0EAF 51%, #AE0DB2 100%)",
                        }}
                      >
                        Deposit crypto
                      </button>
                    ) : null}
                    {showBackHome ? (
                      <button
                        type="button"
                        onClick={handleGoHome}
                        className="w-full h-[44px] rounded-full text-[14px] font-semibold text-text-base border border-border-subtle bg-transparent"
                      >
                        Back to Home
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : showForm ? (
              <>
                <div
                  className="px-[20px] py-[18px] text-white shrink-0"
                  style={{
                    background:
                      "linear-gradient(135deg, #2B2F6B 0%, #4A3F7A 55%, #5F5696 100%)",
                  }}
                >
                  <h2 className="text-[12px] font-bold">Order summary</h2>
                  <div className="mt-[12px] grid grid-cols-2 gap-x-[16px] gap-y-[10px] text-[10px]">
                    {[
                      {
                        label: isPayout ? "Payout Amount" : "Purchase Amount",
                        value: `${currencyPrefix(instanceData?.fiatCurrencySymbol)}${formatAmount(instanceData?.fiatAmount)}`,
                      },
                      {
                        label: "Exchange rate",
                        value: (
                          <button
                            type="button"
                            onClick={() => setShowInverseRate((prev) => !prev)}
                            className="inline-flex items-center gap-[4px] text-left"
                            aria-label="Show the inverse exchange rate"
                          >
                            <span>1 {rateBaseSymbol}</span>
                            <span className="material-symbols-outlined text-[12px]">
                              sync_alt
                            </span>
                            <span>
                              {formatAmount(quotedRate, 4)} {rateQuoteSymbol}
                            </span>
                          </button>
                        ),
                      },
                      {
                        label: isPayout ? "Crypto to send" : "You receive",
                        value: `${formatAmount(
                          instanceData?.fiatCurrencySymbol === "CAD" || isPayout
                            ? instanceData?.toReleaseAmount
                            : instanceData?.cryptoAmount,
                        )} ${instanceData?.cryptoCurrencySymbol || ""}`,
                      },
                      ...(instanceData?.fees
                        ? [
                            {
                              label: "Service fee",
                              value: `${currencyPrefix(instanceData?.fiatCurrencySymbol)}${formatAmount(instanceData?.fees)}`,
                            },
                          ]
                        : []),
                      {
                        label: "Final Payment Amount",
                        value: `${currencyPrefix(instanceData?.fiatCurrencySymbol)}${formatAmount(instanceData?.fiatAmount)}`,
                      },
                    ].map((item) => (
                      <div key={item.label}>
                        <p className="text-[#BEC0CD] font-medium">
                          {item.label}
                        </p>
                        <div className="mt-[2px] font-semibold">
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                  <div className="flex-1 overflow-y-auto px-[20px] py-[16px] space-y-[14px]">
                    <div>
                      {sectionTitle("User Details:")}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px]">
                        <GlobalTextInputField
                          title="First Name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="First name"
                          inputBorder="border-border-subtle rounded-lg"
                          readonly={locked.firstName}
                          disabled={locked.firstName}
                        />
                        <GlobalTextInputField
                          title="Last Name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Last name"
                          inputBorder="border-border-subtle rounded-lg"
                          readonly={locked.lastName}
                          disabled={locked.lastName}
                        />
                        <GlobalTextInputField
                          title="Email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="email ID"
                          inputBorder="border-border-subtle rounded-lg"
                          readonly={locked.email}
                          disabled={locked.email}
                        />
                        <DateOfBirthPicker
                          value={dob}
                          onChange={setDob}
                          disabled={locked.dob}
                        />
                        {locked.country ? (
                          <GlobalTextInputField
                            title="Country of Residence"
                            value={
                              residenceCountry?.country ||
                              instanceData?.userDetails?.country ||
                              ""
                            }
                            prefixIcon={
                              instanceData?.userCountryImage ? (
                                <img
                                  src={instanceData.userCountryImage}
                                  alt="country"
                                  className="w-[14px] h-[14px] object-contain"
                                />
                              ) : undefined
                            }
                            prefixIconPadding="pl-[28px]"
                            onChange={() => {}}
                            inputBorder="border-border-subtle rounded-lg"
                            readonly
                            disabled
                          />
                        ) : (
                          <CountriesDropdown
                            listOfValues={countries}
                            value={residenceCountryId}
                            onChange={setResidenceCountryId}
                            label="Country of Residence"
                            placeholder="Select country"
                            enableSearch
                            {...dropdownProps}
                          />
                        )}
                        {locked.countryOfBirth ? (
                          <GlobalTextInputField
                            title="Country of Birth"
                            value={
                              birthCountry?.country ||
                              instanceData?.userDetails?.countryOfBirth ||
                              instanceData?.userDetails?.countryOfBirthName ||
                              ""
                            }
                            prefixIcon={
                              instanceData?.userCountryOfBirthImage ? (
                                <img
                                  src={instanceData.userCountryOfBirthImage}
                                  alt="country"
                                  className="w-[14px] h-[14px] object-contain"
                                />
                              ) : undefined
                            }
                            prefixIconPadding="pl-[28px]"
                            onChange={() => {}}
                            inputBorder="border-border-subtle rounded-lg"
                            readonly
                            disabled
                          />
                        ) : (
                          <CountriesDropdown
                            listOfValues={countries}
                            value={birthCountryId}
                            onChange={setBirthCountryId}
                            label="Country of Birth"
                            placeholder="Select country"
                            enableSearch
                            {...dropdownProps}
                          />
                        )}
                        {instanceData?.userDetails?.mobile ? (
                          <GlobalTextInputField
                            title="Mobile"
                            type="tel"
                            value={instanceData.userDetails.mobile}
                            onChange={() => {}}
                            inputBorder="border-border-subtle rounded-lg"
                            readonly
                            disabled
                          />
                        ) : null}
                        {instanceData?.userDetails?.province ? (
                          <GlobalTextInputField
                            title="Province"
                            value={instanceData.userDetails.province}
                            onChange={() => {}}
                            inputBorder="border-border-subtle rounded-lg"
                            readonly
                            disabled
                          />
                        ) : null}
                      </div>
                    </div>

                    <div>
                      {sectionTitle("Delivery Details:")}
                      {isExternal ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px]">
                          <div className="sm:col-span-2">
                            {instanceData?.isBuying == 1 && (
                              <GlobalTextInputField
                                title="Wallet Address"
                                value={
                                  instanceData?.walletDetails?.address || "-"
                                }
                                onChange={() => {}}
                                inputBorder="border-border-subtle rounded-lg"
                                readonly
                                disabled
                              />
                            )}
                          </div>
                          <GlobalTextInputField
                            title="Network"
                            value={
                              instanceData?.walletDetails?.network ||
                              instanceData?.cryptoSettlementDetails?.Network ||
                              "-"
                            }
                            prefixIcon={
                              instanceData?.cryptoSettlementDetails
                                ?.networkImage ? (
                                <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-white overflow-hidden">
                                  <img
                                    src={
                                      instanceData.cryptoSettlementDetails
                                        .networkImage
                                    }
                                    alt={
                                      instanceData?.walletDetails?.network ||
                                      "network"
                                    }
                                    className="w-[14px] h-[14px] object-contain"
                                  />
                                </span>
                              ) : undefined
                            }
                            prefixIconPadding="pl-[28px]"
                            onChange={() => {}}
                            inputBorder="border-border-subtle rounded-lg"
                            readonly
                            disabled
                          />
                          <GlobalTextInputField
                            title="Settlement Currency"
                            value={instanceData?.cryptoCurrencySymbol || "-"}
                            prefixIcon={
                              instanceData?.cryptoCurrencyImage ? (
                                <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-white overflow-hidden">
                                  <img
                                    src={instanceData.cryptoCurrencyImage}
                                    alt={
                                      instanceData?.cryptoCurrencySymbol ||
                                      "currency"
                                    }
                                    className="w-[14px] h-[14px] object-contain"
                                  />
                                </span>
                              ) : undefined
                            }
                            prefixIconPadding="pl-[28px]"
                            onChange={() => {}}
                            inputBorder="border-border-subtle rounded-lg"
                            readonly
                            disabled
                          />
                          {instanceData?.payeeDetails?.iban ? (
                            <div className="sm:col-span-2">
                              <GlobalTextInputField
                                title="IBAN"
                                value={instanceData.payeeDetails.iban}
                                onChange={() => {}}
                                inputBorder="border-border-subtle rounded-lg"
                                readonly
                                disabled
                              />
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px]">
                          <GlobalTextInputField
                            title="Merchant Name"
                            value={
                              instanceData?.merchantDetails?.merchantName || "-"
                            }
                            onChange={() => {}}
                            inputBorder="border-border-subtle rounded-lg"
                            readonly
                            disabled
                          />
                          <GlobalTextInputField
                            title="Merchant of Record"
                            value={
                              instanceData?.merchantDetails?.merchantOfRecord ||
                              instanceData?.merchantDetails?.merchantName ||
                              "-"
                            }
                            onChange={() => {}}
                            inputBorder="border-border-subtle rounded-lg"
                            readonly
                            disabled
                          />
                          <GlobalTextInputField
                            title="Merchant URL"
                            value={
                              instanceData?.merchantDetails?.merchantUrl || "-"
                            }
                            onChange={() => {}}
                            inputBorder="border-border-subtle rounded-lg"
                            readonly
                            disabled
                          />
                          <GlobalTextInputField
                            title="Settlement Type"
                            value={instanceData?.cryptoCurrencySymbol || "-"}
                            onChange={() => {}}
                            inputBorder="border-border-subtle rounded-lg"
                            readonly
                            disabled
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      {sectionTitle("Confirm your wallet")}
                      <div className="flex gap-[10px] items-center">
                        <CheckBox
                          checked={walletConfirmed}
                          onChange={() => setWalletConfirmed((prev) => !prev)}
                          borderColor="border-[#1418A8]"
                        />
                        <p className="text-text-subtle text-[10px] leading-[1.4]">
                          I confirm that I control this wallet and that the
                          wallet address and network are correct.
                        </p>
                      </div>
                    </div>

                    <div>
                      {sectionTitle("Terms and confirmation")}
                      <div className="space-y-[10px]">
                        <div className="flex gap-[10px] items-center">
                          <CheckBox
                            checked={termsAccepted}
                            onChange={() => setTermsAccepted((prev) => !prev)}
                            borderColor="border-[#1418A8]"
                          />
                          <p className="text-text-subtle text-[10px] leading-[1.4]">
                            I agree to the{" "}
                            <button
                              type="button"
                              onClick={() => setTermsOpen(true)}
                              className="text-[#1418A8] underline font-semibold"
                            >
                              End-User Terms & Conditions
                            </button>
                            .
                          </p>
                        </div>
                        <div className="flex gap-[10px] items-center">
                          <CheckBox
                            checked={aupAccepted}
                            onChange={() => setAupAccepted((prev) => !prev)}
                            borderColor="border-[#1418A8]"
                          />
                          <p className="text-text-subtle text-[10px] leading-[1.4]">
                            I agree to the{" "}
                            <button
                              type="button"
                              onClick={() => setAupOpen(true)}
                              className="text-[#1418A8] underline font-semibold"
                            >
                              Acceptable Use Policy
                            </button>
                            .
                          </p>
                        </div>
                        <div className="flex gap-[10px] items-center">
                          <CheckBox
                            checked={riskAccepted}
                            onChange={() => setRiskAccepted((prev) => !prev)}
                            borderColor="border-[#1418A8]"
                          />
                          <p className="text-text-subtle text-[10px] leading-[1.4]">
                            I have read and accept the{" "}
                            <button
                              type="button"
                              onClick={() => setRiskOpen(true)}
                              className="text-[#1418A8] underline font-semibold"
                            >
                              Risk Disclosure
                            </button>
                            .
                          </p>
                        </div>
                        <div className="flex gap-[10px] items-center">
                          <CheckBox
                            checked={accuracyAccepted}
                            onChange={() =>
                              setAccuracyAccepted((prev) => !prev)
                            }
                            borderColor="border-[#1418A8]"
                          />
                          <p className="text-text-subtle text-[10px] leading-[1.4]">
                            I confirm that I am{" "}
                            {isPayout ? "cashing out" : "purchasing"} crypto on
                            my own behalf and that the information provided is
                            accurate.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-[20px] pb-[20px] pt-[8px] shrink-0 space-y-[8px]">
                    {error ? (
                      <div className="text-[12px] text-text-status-red bg-bg-status-red border border-border-button-delete rounded-lg p-[10px]">
                        {error}
                      </div>
                    ) : null}

                    {depositPaymentUrl ? (
                      <button
                        type="button"
                        onClick={() => setDepositOpen(true)}
                        className="w-full h-[44px] rounded-full text-white text-[14px] font-semibold"
                        style={{
                          background:
                            "linear-gradient(90deg, #0E18A7 0%, #5E0EAF 51%, #AE0DB2 100%)",
                        }}
                      >
                        Deposit crypto
                      </button>
                    ) : null}
                    <div className="flex items-center gap-[8px]">
                      <button
                        type="button"
                        onClick={handlePayNow}
                        disabled={!canPay || submitting}
                        className="flex-1 min-w-0 h-[44px] rounded-full text-white text-[14px] font-semibold disabled:opacity-50"
                        style={{
                          background:
                            "linear-gradient(90deg, #0E18A7 0%, #5E0EAF 51%, #AE0DB2 100%)",
                        }}
                      >
                        {submitting
                          ? "Processing..."
                          : isPayout
                            ? "Continue"
                            : "Pay Now"}
                      </button>

                      {showBackHome ? (
                        <button
                          type="button"
                          onClick={handleGoHome}
                          disabled={submitting}
                          className="flex-1 min-w-0 h-[44px] rounded-full text-[14px] font-semibold text-text-base border border-border-subtle bg-transparent disabled:opacity-50"
                        >
                          Back to Home
                        </button>
                      ) : null}
                    </div>
                    <AccessInfoBadges className="mt-0" />
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col">
                <div className="flex-1 flex items-center justify-center p-[24px] text-text-subtle text-[14px] text-center">
                  Unable to load this order state.
                </div>
                {showBackHome ? (
                  <div className="px-[24px] pb-[24px] shrink-0">
                    <button
                      type="button"
                      onClick={handleGoHome}
                      className="w-full h-[44px] rounded-full text-[14px] font-semibold text-text-base border border-border-subtle bg-transparent"
                    >
                      Back to Home
                    </button>
                  </div>
                ) : null}
              </div>
            )}
            {!loading &&
            !(error && !instanceData) &&
            !showStatus &&
            showForm ? null : (
              <div className="px-[20px] pb-[16px] shrink-0">
                <AccessInfoBadges />
              </div>
            )}
          </div>
        </div>
      </div>

      <TermsAndConditionsModal
        isOpen={termsOpen}
        onClose={() => setTermsOpen(false)}
      />
      <AcceptableUsePolicyModal
        isOpen={aupOpen}
        onClose={() => setAupOpen(false)}
      />
      <RiskDisclosureModal
        isOpen={riskOpen}
        onClose={() => setRiskOpen(false)}
      />
      {depositPaymentUrl ? (
        <CryptoDepositPopup
          isOpen={depositOpen}
          paymentUrl={depositPaymentUrl}
          orderId={acceptedDeposit?.orderId}
          onClose={() => setDepositOpen(false)}
          onPaid={() => {
            setDepositOpen(false);
            fetchInstanceDetails();
          }}
        />
      ) : null}
    </div>
  );
}

export default ReviewPurchasePage;
